import { useMemo } from 'react';
import { sortByDesc } from '../../../shared/utils/sortUtils.ts';
import { OperationWithLabel } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { useSevenDaysAgo } from './useSevenDaysAgo.ts';


export function useFetchOperations() {
    const sevenDaysAgo = useSevenDaysAgo();

    const { data: operationsAndLabels, refetch: refetchWithLabel } = trpc.operations.getWithLabel.useQuery();

    const { data: recenthandledOperations, refetch: refetchRecentHandled } = trpc.operations.get.useQuery({
        scrapHandledAt: { start: sevenDaysAgo },
    });


    // Split operations with labels
    const { newOperations, intermediateOperations, heldLabelOperations } = useMemo(() => {
        const converted: OperationWithLabel[] | undefined =
            operationsAndLabels?.map(it => ({
                ...it.operation,
                scrapLabel: it.scrapLabel,
            }));

        const newOperations = converted?.filter(it => !it.scrapReview && !it.scrapCloseForm);
        const heldLabelOperations = converted?.filter(it => it.scrapCloseForm);
        const intermediateOperations = converted?.filter(it => it.scrapReview?.result === 'inspect' && !it.scrapCloseForm);

        if (newOperations) sortByDesc(newOperations, it => it.effectiveTime);
        if (intermediateOperations) sortByDesc(intermediateOperations, it => it.scrapReview!.time ?? it.effectiveTime);
        return { newOperations, intermediateOperations, heldLabelOperations };
    }, [operationsAndLabels]);

    // Merge recent handled operations array with older operations that still have pending labels
    const handledOperations = useMemo(() => {
        if (!recenthandledOperations || !heldLabelOperations) return null;

        const handledOperations = [...heldLabelOperations];// Take held label operations first, because only they have the label field.
        for (const operation of recenthandledOperations) {
            if (!handledOperations.find(it => it._id === operation._id)) {
                handledOperations.push(operation);
            }
        }

        const sorted = sortByDesc([...handledOperations], it => it.scrapHandledAt ?? 0);
        return sorted;
    }, [recenthandledOperations, heldLabelOperations]);

    const refetchOperations = () => {
        refetchWithLabel();
        refetchRecentHandled();
    }


    return {
        newOperations: newOperations as OperationWithLabel[] | undefined,
        intermediateOperations: intermediateOperations as OperationWithLabel[] | undefined,
        handledOperations: handledOperations as OperationWithLabel[] | null,
        refetchOperations,
    };
}
